import { AfterContentInit, Directive, ElementRef, OnDestroy, inject, input } from '@angular/core';
import { ScreenSize } from '../enums';
import { LayoutWidthBreakpoints } from '../interfaces';
import { LayoutService } from '../services';
import { DEFAULT_LAYOUT_WIDTH_BREAKPOINTS } from '../consts';

@Directive({
    selector: '[caResizeObserver]'
})
export class ResizeObserverDirective implements AfterContentInit, OnDestroy {
    caResizeObserver = input<LayoutWidthBreakpoints, LayoutWidthBreakpoints | string>(DEFAULT_LAYOUT_WIDTH_BREAKPOINTS, {
        transform: value => {
            if (!value || typeof value === 'string') {
                return DEFAULT_LAYOUT_WIDTH_BREAKPOINTS;
            }

            return value;
        }
    });

    private resizeObserver: ResizeObserver | undefined;
    private readonly elementRef = inject(ElementRef);
    private readonly layoutService = inject(LayoutService);

    ngAfterContentInit(): void {
        this.initElementResizeObserver();
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;
    }

    private initElementResizeObserver() {
        this.resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const width = entry.contentRect.width;
                const currentBreakpoint = this.getBreakpointByWidth(width);
                this.layoutService.setScreenSize(currentBreakpoint);
            }
        });

        this.resizeObserver.observe(this.elementRef.nativeElement);

        const initialWidth = this.elementRef.nativeElement.getBoundingClientRect().width;
        const currentBreakpoint = this.getBreakpointByWidth(initialWidth);
        this.layoutService.setScreenSize(currentBreakpoint);
    }

    private getBreakpointByWidth(width: number): ScreenSize {
        const breakpoints = this.caResizeObserver();

        if (width <= breakpoints.xSmallMax) {
            return ScreenSize.XSmall;
        }

        if (width <= breakpoints.smallMax) {
            return ScreenSize.Small;
        }

        if (width <= breakpoints.mediumMax) {
            return ScreenSize.Medium;
        }

        if (width <= breakpoints.largeMax) {
            return ScreenSize.Large;
        }

        return ScreenSize.XLarge;
    }
}
